import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Ability, subject } from '@casl/ability';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../shared/services';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { projectDetails, projectAccountsInfo } from '../../../../../../core/projects.selectors';
import { FetchProjectAccountsRequest } from '../../../../../../core/projects.actions';
import { UploadFileRequest } from '../../../../../../../../shared/core/shared.actions';
import { SampleRecordsService } from '../sample-records.service';
import { SampleLocationPopupComponent } from '../sample-location-popup/sample-location-popup.component';
import { SampleTestEntryComponent } from '../sample-test-entry/sample-test-entry.component';

/**
 * Create / edit a Sample Record. The form is driven by the per-org
 * `custom_group` config delivered as `appData.dailyReportFields.sample_record_*`.
 *
 * Layout mirrors the mobile CreateSampleScreen accordion:
 *   - General             (sample_record_general)        — flat fields
 *   - Mix Design          (sample_record_mix_design)     — flat fields
 *   - Sources             (sample_record_sources)        — repeating rows
 *   - Associations        (sample_record_associations)   — repeating rows
 *   - Contract            (sample_record_contract)       — flat (multi_select)
 *   - Tests               (sample_record_tests)          — flat (multi_select)
 *   - Remarks             (sample_record_remarks)        — repeating rows
 *
 * Save POST/PATCHes /v1/sample-record. "Push to AASHTOWare" calls /push.
 * Both surface errors via CommonService.notification.
 *
 * Read-only mode: when the acl grants no SampleRecord update on the project
 * (org web read-only mode masks every role to project owner, and org-level
 * viewers only ever hold read) the same sections render as view fields, the
 * action bar is hidden and the test-entry popup opens as a viewer.
 */
@Component({
  selector: 'app-create-or-edit-sample-record',
  templateUrl: './create-or-edit-sample-record.component.html',
  styleUrls: ['./create-or-edit-sample-record.component.scss'],
  standalone: false,
})
export class CreateOrEditSampleRecordComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @Input() data: any = {};
  /**
   * When the form is opened from inside a DWR (vs. the standalone Sample
   * Records tab), pass the surrounding context so the saved row gets linked
   * to the daily report + bid item + material set. When unset, behavior is
   * identical to the original standalone flow.
   *
   * Also accepted via `data.embeddedContext` so callers that go through
   * `projectsService.openRightPanel` (which only sets `instance.data`) can
   * still supply it without changes to the right-panel host.
   */
  @Input() embeddedContext?: {
    dailyReportId: number | string;
    bidItemId?: number | string;
    bidItemMaterialSetId?: number | string;
    /** The material card the sample was started from; seeds the Material picker on create. */
    materialId?: number | string;
  };

  projectId: string;
  projectTimezone: string;
  account: any;

  // inject() rather than a constructor param: the repo's lint flags
  // constructor injection, so a new dependency shouldn't add a violation.
  private readonly ability = inject(Ability);
  /** SampleRecord update on this project per the current abilities. */
  canUpdate = false;
  /** Viewer mode — no inputs, no action bar. See class doc. */
  readOnly = false;

  // Live record (from PATCH/GET response). When `data?.uuid` is set we PATCH;
  // otherwise we POST. After a successful create we promote the response into
  // `record` so the next save updates the same row.
  record: any = null;
  loading = false;
  saving = false;
  pushing = false;
  errorMessage: string | null = null;

  // Config-driven form
  groupConfigs: any[] = [];
  // Flat form for non-repeating fields. Repeating-group rows live in `rows`
  // keyed by group key (e.g. rows.sources, rows.associations, rows.remarks);
  // each entry is a child FormGroup so the same form-field components used
  // for the flat fields work without modification.
  form = new FormGroup<any>({});
  rows: Record<string, FormGroup[]> = {};
  // For each field name, the resolved pick-list options for the <app-dropdown>.
  pickListOptions: Record<string, any[]> = {};
  // Per-row pick-list options keyed by `${groupKey}-${rowIdx}-${fieldName}`.
  rowPickListOptions: Record<string, any[]> = {};

  // Attachment state
  pictures: any[] = [];

  // Materials picker — fetched once per project from /v1/sample-record/materials.
  // Drives the Material dropdown in the General → Context sub-section.
  materialOptions: { label: string; value: number }[] = [];

  // Source picker — fetched from /v1/sample-record/sources, narrowed to
  // sources approved for the currently-picked material per AASHTOWare's
  // SourceMaterials approval list. When no material is set, returns the
  // org's full source list. Refreshed whenever `material_id` changes;
  // when non-empty, overrides the lookup-derived `source_pit` pool in
  // resolvePickListOptions for the `source_id` field.
  private sourceOptions: { label: string; value: any }[] = [];

  // Test-method picker — fetched from /v1/sample-record/test-methods,
  // narrowed to the org_material_test lookups with a matching
  // `material_test_requirement` rule for the currently-picked material
  // (and optionally sample_type). When no material is set, returns the
  // org's full test-method list. Refreshed whenever `material_id` or
  // `sample_type` changes; when non-empty, overrides the lookup-derived
  // `org_material_test` pool in resolvePickListOptions for the
  // `material_test_ids` field.
  private testMethodOptions: { label: string; value: any }[] = [];

  // Accordion expand state. First section opens by default; rest are collapsed
  // until tapped. Sub-sections (within General) are independently togglable.
  expandedGroups: Record<string, boolean> = {};
  expandedSubsections: Record<string, boolean> = {};

  private appData: any = null;
  private projectData: any = null;
  // Project members — used as the Sampler ID-Name pick list source.
  private projectAccounts: any[] = [];
  private formChangesSubscribed = false;
  private materialIdChangesSubscribed = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private sampleRecordsService: SampleRecordsService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  /**
   * Walk the ActivatedRoute tree looking for a `projectId` param. Right-panel
   * hosts mount us above the `:projectId` route segment, so `route.snapshot`
   * alone doesn't carry it. As a final fallback parse the URL — the project
   * route is always `/projects/<uuid>(/...)?`.
   */
  private resolveProjectId(): string | undefined {
    const fromTree = (r: ActivatedRoute | null): string | undefined => {
      if (!r) return undefined;
      const v = r.snapshot.paramMap.get('projectId');
      if (v) return v;
      for (const c of r.children) {
        const found = fromTree(c);
        if (found) return found;
      }
      return undefined;
    };
    let id = fromTree(this.route);
    if (!id) {
      // Walk to root and back down to catch the case where this component's
      // ActivatedRoute sits ABOVE the `:projectId` node (right-panel host).
      let root: ActivatedRoute = this.route;
      while (root.parent) root = root.parent;
      id = fromTree(root);
    }
    if (!id) {
      const m = (this.router?.url || '').match(/\/projects\/([^/?#]+)/);
      if (m) id = m[1];
    }
    return id;
  }

  ngOnInit(): void {
    // This component is instantiated dynamically via `projectsService.openRightPanel`,
    // so its ActivatedRoute resolves to the projects-wrapper level (ABOVE the
    // `:projectId` segment) — the snapshot paramMap doesn't carry projectId. Walk
    // the route tree to find the descendant that does. Falls back to parsing
    // router.url so the right panel still works even when this component is
    // mounted before child routes are activated.
    this.projectId = this.resolveProjectId();
    this.account = this.authService.getCurrentUser();
    this.refreshAccess();

    if (this.projectId) {
      this.sampleRecordsService
        .materials(this.projectId)
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: (resp: any) => {
            const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
            this.materialOptions = rows.map((m: any) => ({
              label: m?.name || m?.description || `(material ${m?.id})`,
              value: Number(m?.id),
            }));
            // A sample started from a DWR material card is seeded with that
            // material's uuid (the JSON-API shape the dialog holds); the picker
            // is keyed by the numeric id, so swap it in once the rows arrive.
            // emitEvent stays on so the source / test-method pickers narrow.
            const bound = this.form?.get('material_id')?.value;
            const byUuid = typeof bound === 'string' && rows.find((m: any) => m?.uuid === bound);
            if (byUuid) {
              this.form.get('material_id')!.setValue(Number(byUuid.id));
            }
            // Guarantee the currently-bound material_id has a matching option
            // even if it wasn't returned by /materials (e.g. material has been
            // disabled since save). Otherwise mat-select renders empty.
            const currentId = this.form?.get('material_id')?.value ?? this.data?.material_id;
            if (currentId !== null && currentId !== undefined
              && !this.materialOptions.some(o => o.value === Number(currentId))) {
              this.materialOptions = [
                { label: this.data?.material_name || `(material ${currentId})`, value: Number(currentId) },
                ...this.materialOptions,
              ];
            }
          },
          error: () => { /* non-fatal — picker just stays empty */ },
        });

      // Initial source list (no materialId yet → full org list). The
      // material_id valueChanges subscription below refetches when the
      // user picks/changes a material so the picker narrows to approved.
      this.fetchSourceOptions(this.data?.material_id ?? null, this.data?.sample_date ?? null);

      // Initial test-method list. Mirrors fetchSourceOptions — narrows
      // to material_test_requirement-backed methods once material_id is
      // picked; valueChanges subscriptions on material_id and sample_type
      // below refetch on changes.
      this.fetchTestMethodOptions(this.data?.material_id ?? null, this.data?.sample_type ?? null);
    }

    // Allow callers that go through projectsService.openRightPanel to pass
    // the embedded context inside `data.embeddedContext` (the right-panel
    // host only assigns `instance.data` / `instance.options`).
    if (!this.embeddedContext && this.data?.embeddedContext) {
      this.embeddedContext = this.data.embeddedContext;
    }

    this.store
      .select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.appData = data;
        // Abilities are replaced once /appdata loads, so re-read the grant here.
        this.refreshAccess();
        this.buildGroupConfigs();
        this.initializeForm();
      });

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((p: any) => {
        this.projectData = p;
        if (p?.timezone) this.projectTimezone = p.timezone;
        if (this.groupConfigs?.length) {
          this.recomputePickListOptions();
        }
      });

    // Project members back the Sampler ID-Name picker. Dispatch a fetch on
    // open and re-resolve pick-list options when the list arrives so the
    // dropdown populates without a manual refresh.
    if (this.projectId) {
      this.store.dispatch(FetchProjectAccountsRequest({ payload: { projectId: this.projectId } }));
    }
    this.store
      .select(projectAccountsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((info: any) => {
        this.projectAccounts = Array.isArray(info?.list) ? info.list : [];
        if (this.groupConfigs?.length) {
          this.recomputePickListOptions();
        }
      });

    // Hydrate pictures from the existing sample row's joined `pictures` array
    // (the GET endpoint joins from the attachment table).
    if (Array.isArray(this.data?.pictures)) {
      this.pictures = this.data.pictures.map((p: any) => this.normalizePicture(p));
      this.publishMediaData();
    }

    // If we have a uuid, refetch the full record (sources/associations/etc.
    // are NOT in the list payload).
    if (this.data?.uuid) {
      this.loadFullRecord(this.data.uuid);
    } else {
      this.record = null;
    }

    // Receive uploaded picture metadata back from the shared upload effect.
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const items = Array.isArray(data) ? data : [data];
        items.forEach((item: any) => {
          if (item?.fileType === 'picture' || item?.fileType === 'document') {
            // Only claim attachments uploaded for THIS form (no `tags`).
            if (!item?.tags?.length) {
              this.pictures.push(item);
            }
          }
        });
        this.publishMediaData();
      });
  }

  /**
   * Re-evaluate edit access. Callers pass `data.readOnly` to force viewer
   * mode (e.g. an embedded list that is itself read-only); otherwise it is
   * the acl's SampleRecord update grant on this project.
   */
  private refreshAccess(): void {
    this.canUpdate = !!this.projectId
      && this.ability.can('update', subject('SampleRecord', { project_id: this.projectId }));
    this.readOnly = !!this.data?.readOnly || !this.canUpdate;
    this.applyReadOnlyState();
  }

  /** Keep every control disabled in viewer mode so nothing edits by accident. */
  private applyReadOnlyState(): void {
    if (!this.readOnly) return;
    this.form.disable({ emitEvent: false });
    Object.values(this.rows).forEach(groupRows => groupRows.forEach(r => r.disable({ emitEvent: false })));
  }

  /**
   * Viewer-mode value for a field: pick lists resolve to their option label,
   * multi-selects join their labels, dates print as "Sep 22, 2026", and the
   * display-only aliases (sample_id → name, …) go through displayValueFor.
   */
  readOnlyValueFor(field: any, group?: any, rowIdx?: number): string {
    if (!field) return '-';
    if (group === undefined && this.isDisplayOnly(field)) return this.displayValueFor(field);
    const control = group !== undefined && rowIdx !== undefined
      ? this.rows[group.key]?.[rowIdx]?.get(field.name)
      : this.form.get(field.name);
    let value = control?.value;
    if ((value === undefined || value === null || value === '') && group === undefined) {
      value = this.data?.[field.name];
    }
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) {
      return '-';
    }
    if (Array.isArray(value)) {
      return value.map(v => this.chipLabel(field, v, group, rowIdx)).join(', ');
    }
    if (this.fieldHasPickList(field)) {
      return this.chipLabel(field, value, group, rowIdx);
    }
    if (this.isDate(field)) {
      const parsed = moment(value, [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY'], true);
      return parsed.isValid() ? parsed.format('MMM D, YYYY') : String(value);
    }
    return String(value);
  }

  /** Material name for the viewer-mode Material field. */
  materialDisplayName(): string {
    const id = this.form?.get('material_id')?.value ?? this.data?.material_id;
    const match = this.materialOptions.find(o => String(o.value) === String(id));
    return match?.label || this.data?.material_name || '-';
  }

  /**
   * <app-pictures-section-with-tags> drives its own picture list from the
   * `projectsService.mediaData` subject (see its ngOnChanges — it ignores the
   * `[pictures]` input and instead listens to mediaData). Republish whenever
   * we mutate `this.pictures` so the shared component renders thumbnails on
   * hydrate, after upload, and after delete.
   */
  private publishMediaData(): void {
    setTimeout(() => this.projectsService.mediaData.next(this.pictures));
  }

  /** Build the ordered list of sample-record sections from appData. */
  private buildGroupConfigs(): void {
    const dr = this.appData?.dailyReportFields || {};
    const order = [
      { key: 'general', label: 'General', src: dr.sample_record_general },
      { key: 'mix_design', label: 'Mix design information', src: dr.sample_record_mix_design },
      { key: 'sources', label: 'Sources', src: dr.sample_record_sources },
      { key: 'associations', label: 'Associations', src: dr.sample_record_associations },
      { key: 'contract', label: 'Contract', src: dr.sample_record_contract },
      { key: 'tests', label: 'Tests', src: dr.sample_record_tests },
      { key: 'remarks', label: 'Remarks', src: dr.sample_record_remarks },
    ];
    this.groupConfigs = order
      .filter(o => !!o.src)
      .map(o => ({
        key: o.key,
        label: o.label,
        ui_config: o.src.ui_config || {},
        fields: (o.src.fields || []).slice().sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)),
      }));
    // Default: first section expanded, others collapsed (matches the mobile
    // CreateSampleScreen accordion behavior).
    this.groupConfigs.forEach((g, i) => {
      if (this.expandedGroups[g.key] === undefined) {
        this.expandedGroups[g.key] = i === 0;
      }
    });
  }

  toggleGroup(key: string): void {
    this.expandedGroups[key] = !this.expandedGroups[key];
  }

  toggleSubsection(key: string): void {
    this.expandedSubsections[key] = !this.expandedSubsections[key];
  }

  isGroupExpanded(key: string): boolean {
    return !!this.expandedGroups[key];
  }

  isSubsectionExpanded(key: string): boolean {
    // Default: expanded unless explicitly collapsed.
    return this.expandedSubsections[key] !== false;
  }

  /**
   * For the General section, group fields by ui_config.section_name so we can
   * render each as its own sub-accordion. Fields without a section_name (or
   * where the section is 'context') still render but as a collapsed-by-default
   * sub-section. Location-section fields are filtered out — they live in the
   * popup card outside the accordion.
   */
  generalSubsections(group: any): { key: string; label: string; fields: any[] }[] {
    if (group?.key !== 'general') return [];
    const buckets: Record<string, any[]> = {};
    const order: string[] = [];
    (group.fields || []).forEach((f: any) => {
      if (this.isLocationSectionField(f)) return;
      const name = f?.ui_config?.section_name || 'general';
      if (!buckets[name]) {
        buckets[name] = [];
        order.push(name);
      }
      buckets[name].push(f);
    });
    const labelize = (s: string) => s
      .replace(/_/g, ' ')
      .replace(/^./, c => c.toUpperCase());
    return order.map(name => ({
      key: `general-${name}`,
      label: labelize(name),
      fields: buckets[name],
    }));
  }

  /**
   * Build the `form` controls + `rows` arrays from the group config.
   * Hydrates from `this.data` (the row from the list) when editing.
   */
  /**
   * Sample-record context, mirrors the mobile CreateSampleScreen `buildContext`.
   * Drives `ui_config.default_from: "context.<key>"` defaults — most importantly
   * `sampler_id` which seeds from the AASHTOWare username on the logged-in
   * account (`source_entity_extra_attributes.user_name = "DOT\\<user>"`).
   */
  private buildContext(): Record<string, any> {
    const acct: any = this.account || {};
    const aashtoUserName: string = acct?.source_entity_extra_attributes?.user_name || '';
    const samplerName = aashtoUserName
      ? String(aashtoUserName).split('\\').pop()
      : (acct?.username || (acct?.email ? String(acct.email).split('@')[0] : '') || '');
    return {
      sampler_id: samplerName || undefined,
      sample_status: 'In Testing',
    };
  }

  private resolveContextValue(spec: string, ctx: Record<string, any>): any {
    if (typeof spec !== 'string') return undefined;
    if (spec.startsWith('context.')) return ctx[spec.slice('context.'.length)];
    if (spec === 'today') return new Date();
    return undefined;
  }

  private initializeForm(): void {
    if (!this.groupConfigs?.length) return;

    // Track which control names are still in scope for this run so anything
    // left over (e.g. a control whose field was removed from custom_group
    // config) gets pruned at the end. We intentionally do NOT clear the
    // FormGroup up-front — replacing controls with `removeControl` +
    // `addControl` leaves [formControlName] directives bound to the old
    // (detached) control reference, so the freshly-hydrated value never
    // reaches the rendered input. Instead, we addControl for fresh names and
    // setValue for existing ones so the directive keeps its live binding.
    const activeNames = new Set<string>();
    // Always carry `material_id` (added below the field loop) and
    // `test_overrides` (the per-test edits map).
    activeNames.add('material_id');
    activeNames.add('test_overrides');

    this.rows = {};

    const ctx = this.buildContext();

    const aliasMap: Record<string, string> = {
      source_id: 'source_value',
      sampler_id: 'sampler_value',
      mix_design_id: 'mix_design_value',
      material_code: 'material_name',
      sample_status: 'ref_sample_status',
      sample_id: 'name',
      field_number: 'control_number',
    };

    this.groupConfigs.forEach(group => {
      if (group?.ui_config?.repeating) {
        const rowKey = group.ui_config.row_field || group.key;
        const initial = Array.isArray(this.data?.[rowKey]) ? [...this.data[rowKey]] : [];
        this.rows[group.key] = initial.map(r => this.buildRowFormGroup(group.fields || [], r));
        return;
      }
      (group.fields || []).forEach((f: any) => {
        activeNames.add(f.name);
        const aliased = aliasMap[f.name];
        let seedValue = this.data?.[f.name] ?? (aliased ? this.data?.[aliased] : undefined);

        // Multi-select hydration aliases:
        //   material_test_ids[]       ← data.tests[].material_test_value
        //   additional_bid_item_ids[] ← data.contract_items[].bid_item_id
        //   additional_source_ids[]   ← data.sources[].source_value
        // The Sources / Contract groups are flat multi-selects, not repeating
        // groups, so hydrate the picker value array from the joined child rows
        // the api returns on getFullRecord. Contract items carry the
        // numeric bid_item_id; the bid-items picker options are keyed by uuid
        // (projectData.bidSheet.bidItems[].id) — map via source_entity_id is
        // also possible but the simpler win is the local id → uuid lookup.
        if (seedValue === undefined || seedValue === null) {
          if (f.name === 'material_test_ids' && Array.isArray(this.data?.tests)) {
            seedValue = this.data.tests
              .map((t: any) => t?.material_test_value)
              .filter((v: any) => !!v);
          }
          if (f.name === 'additional_bid_item_ids' && Array.isArray(this.data?.contract_items)) {
            // sample_record_contract_item.bid_item_id is the LOCAL numeric pk;
            // the bid-items picker pool uses uuid values
            // (projectData.bidSheet.bidItems[].id is the bid_item's uuid, NOT
            // the numeric pk — JSON-API hides the pk). To match by identity,
            // the api's getFullRecord now JOINs and returns `bid_item_uuid`
            // alongside the numeric id; prefer that so the chip resolves to
            // the real picker option (label "201 - Clearing & Grubbing").
            // Falls through to the numeric pk so older api versions or rows
            // missing the join still render (ensureValueInOptions will
            // synthesize a placeholder labeled with the raw id).
            seedValue = this.data.contract_items
              .map((ci: any) => ci?.bid_item_uuid ?? ci?.bid_item_id)
              .filter((v: any) => v !== undefined && v !== null);
          }
          if (f.name === 'additional_source_ids' && Array.isArray(this.data?.sources)) {
            seedValue = this.data.sources
              .map((s: any) => s?.source_value)
              .filter((v: any) => v !== undefined && v !== null && v !== '');
          }
        }

        // Honor ui_config.default_from / derived_from (mirrors the mobile
        // buildInitialFormState). Lets sampler_id default to the AASHTOWare
        // username from the logged-in account, sample_date default to today, etc.
        // Only on create: an existing record's empty field is empty, not
        // "today" / "me" (the viewer would otherwise show a made-up date).
        if (!this.data?.uuid && (seedValue === undefined || seedValue === null || seedValue === '')) {
          const ui = f?.ui_config || {};
          const spec = ui.derived_from || ui.default_from;
          if (spec) {
            const resolved = this.resolveContextValue(spec, ctx);
            if (resolved !== undefined && resolved !== null && resolved !== '') {
              seedValue = resolved;
            }
          }
        }

        const initialValue = this.coerceFormValue(f, seedValue);
        // `form.get`, not `form.contains`: contains() is false for a disabled
        // control, and the viewer keeps the whole form disabled — addControl
        // would then silently no-op and the existing control keep its old value.
        if (this.form.get(f.name)) {
          // Reuse the existing FormControl so any [formControlName] directive
          // bound to this slot keeps its live reference. `setValue` accepts
          // emitEvent so we skip noisy valueChanges from this batch update.
          this.form.get(f.name)!.setValue(initialValue, { emitEvent: false });
        } else {
          this.form.addControl(f.name, new FormControl(initialValue), { emitEvent: false });
        }
      });
    });

    // Always carry a `material_id` control so the Material picker (rendered
    // alongside the General → Context sub-section) can edit it. Hydrate from
    // the existing record's numeric pk on edit. Like the field controls above
    // we setValue when the control already exists so [formControlName] doesn't
    // detach mid-flight.
    const seedMaterialId = this.data?.material_id
      ?? (!this.data?.uuid ? this.embeddedContext?.materialId : undefined)
      ?? null;
    if (this.form.get('material_id')) {
      this.form.get('material_id')!.setValue(seedMaterialId, { emitEvent: false });
    } else {
      this.form.addControl(
        'material_id',
        new FormControl(seedMaterialId),
        { emitEvent: false },
      );
    }

    // `test_overrides` is the per-test edits map keyed by material_test_value.
    // The mobile CreateSampleScreen carries this in formData; we mirror that
    // here so the side-panel SampleTestEntryComponent can stash its onSave
    // edits, and buildPayload() can ship them. On hydrate we reconstruct the
    // map from the saved `tests[]` array (mobile lines ~599-612 in
    // CreateSampleScreen.js).
    const initialOverrides: Record<string, any> = {};
    const savedTests: any[] = Array.isArray(this.data?.tests) ? this.data.tests : [];
    savedTests.forEach((t: any) => {
      if (!t?.material_test_value) return;
      const { material_test_value, ...rest } = t;
      initialOverrides[material_test_value] = {
        ...(initialOverrides[material_test_value] || {}),
        ...rest,
      };
    });
    if (this.form.get('test_overrides')) {
      this.form.get('test_overrides')!.setValue(initialOverrides, { emitEvent: false });
    } else {
      this.form.addControl(
        'test_overrides',
        new FormControl(initialOverrides),
        { emitEvent: false },
      );
    }

    // Prune any controls that are no longer in the active set (e.g. a field
    // got removed from custom_group config between mount and re-init). Done
    // after the add/setValue loop so we never strip a control we're about to
    // re-add — only those that have truly disappeared.
    Object.keys(this.form.controls).forEach(k => {
      if (!activeNames.has(k)) {
        this.form.removeControl(k, { emitEvent: false });
      }
    });

    this.recomputePickListOptions();
    this.applyReadOnlyState();

    if (!this.formChangesSubscribed) {
      this.formChangesSubscribed = true;
      this.form.valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => this.recomputePickListOptions());
    }

    // Refetch the source picker whenever the user picks/changes a material
    // so the dropdown narrows to AASHTOWare-approved sources. Guarded by
    // distinctUntilChanged so the umbrella `form.valueChanges` (above)
    // touching unrelated controls doesn't trigger a refetch.
    if (!this.materialIdChangesSubscribed && this.form.contains('material_id')) {
      this.materialIdChangesSubscribed = true;
      this.form.get('material_id')!
        .valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
        .subscribe((materialId: any) => {
          this.fetchSourceOptions(materialId, this.form.get('sample_date')?.value);
          this.fetchTestMethodOptions(materialId, this.form.get('sample_type')?.value);
        });
    }

    // Same treatment for sample_date — AASHTOWare's SourceMaterials carry
    // effective/expiration windows, so the picker should re-narrow if the
    // user adjusts the date. Pulled into its own subscription so we don't
    // refetch on every unrelated form-field edit.
    if (this.form.contains('sample_date')) {
      this.form.get('sample_date')!
        .valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
        .subscribe((sampleDate: any) =>
          this.fetchSourceOptions(this.form.get('material_id')?.value, sampleDate));
    }

    // sample_type narrows the test-method requirement set (some rules are
    // gated to ACCEPTANCE / VERIFICATION / etc.). Mirror the sample_date
    // subscription so the Tests picker re-narrows on change.
    if (this.form.contains('sample_type')) {
      this.form.get('sample_type')!
        .valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
        .subscribe((sampleType: any) =>
          this.fetchTestMethodOptions(this.form.get('material_id')?.value, sampleType));
    }
  }

  /**
   * Fetch the source picker options from the api, narrowed to sources
   * approved for `materialId` (and not expired as of `sampleDate`) when
   * set. Stashes the result on `this.sourceOptions` and triggers a
   * pick-list recompute so the `source_id` dropdown refreshes.
   */
  private fetchSourceOptions(
    materialId: number | string | null | undefined,
    sampleDate: string | null | undefined = null,
  ): void {
    if (!this.projectId) return;
    this.sampleRecordsService
      .sources(this.projectId, materialId ?? undefined, sampleDate ?? undefined)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
          this.sourceOptions = rows.map((item: any) => ({
            label: item?.name || item?.value || String(item),
            value: item?.value ?? item?.name,
          }));
          this.recomputePickListOptions();
        },
        error: () => {
          // non-fatal — fall through to the lookup-derived pool by
          // clearing our override list. resolvePickListOptions only
          // consults sourceOptions when non-empty.
          this.sourceOptions = [];
          this.recomputePickListOptions();
        },
      });
  }

  /**
   * Fetch the test-method picker options from the api, narrowed to
   * methods with a `material_test_requirement` rule for `materialId`
   * (and `sampleType` when set). Stashes the result on
   * `this.testMethodOptions` and triggers a pick-list recompute so the
   * `material_test_ids` dropdown refreshes.
   */
  private fetchTestMethodOptions(
    materialId: number | string | null | undefined,
    sampleType: string | null | undefined = null,
  ): void {
    if (!this.projectId) return;
    this.sampleRecordsService
      .testMethods(this.projectId, materialId ?? undefined, sampleType ?? undefined)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
          this.testMethodOptions = rows.map((item: any) => ({
            label: item?.name || item?.value || String(item),
            // Other lookup options carry the org_material_test row id on
            // extra_attributes.material_test_id; fall back to value when
            // absent so the picker still works against the lookup payload.
            value: item?.extra_attributes?.material_test_id ?? item?.value ?? item?.name,
          }));
          this.recomputePickListOptions();
        },
        error: () => {
          // non-fatal — fall through to the lookup-derived pool by
          // clearing our override list. resolvePickListOptions only
          // consults testMethodOptions when non-empty.
          this.testMethodOptions = [];
          this.recomputePickListOptions();
        },
      });
  }

  /** Date fields: dropdowns store strings, so coerce ISO/date in. */
  private coerceFormValue(field: any, value: any): any {
    if (value === undefined || value === null) {
      if (field?.ui_config?.multi_select) return [];
      return null;
    }
    return value;
  }

  /** Build a FormGroup for a single repeating-group row. */
  private buildRowFormGroup(fields: any[], seed: any = {}): FormGroup {
    const controls: Record<string, FormControl> = {};
    fields.forEach(f => {
      const v = seed?.[f.name];
      controls[f.name] = new FormControl(this.coerceFormValue(f, v));
    });
    // Carry id/uuid/sequence through so updates target the same child row.
    if (seed?.id) controls.id = new FormControl(seed.id);
    if (seed?.uuid) controls.uuid = new FormControl(seed.uuid);
    if (seed?.sequence !== undefined) controls.sequence = new FormControl(seed.sequence);
    const fg = new FormGroup(controls);
    // Recompute pick-list options when the row's parent field changes (so
    // a downstream filter_by/pick_list_by dropdown reflects the new parent).
    fg.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.recomputePickListOptions();
    });
    return fg;
  }

  /**
   * Resolve pick-list options for every dropdown field, plus per-row
   * options for repeating-group rows. Keeps the dropdown component dumb
   * (it just consumes a flat options array).
   */
  private recomputePickListOptions(): void {
    this.pickListOptions = {};
    this.rowPickListOptions = {};

    const formValues = this.form.getRawValue();

    this.groupConfigs.forEach(group => {
      if (group?.ui_config?.repeating) {
        const rows = this.rows[group.key] || [];
        rows.forEach((row, idx) => {
          const rowValues = row.getRawValue();
          (group.fields || []).forEach((f: any) => {
            if (!this.fieldHasPickList(f)) return;
            const opts = this.resolvePickListOptions(f, rowValues);
            this.rowPickListOptions[`${group.key}-${idx}-${f.name}`] = this.ensureValueInOptions(
              opts,
              rowValues?.[f.name],
              !!f?.ui_config?.multi_select,
            );
          });
        });
        return;
      }
      (group.fields || []).forEach((f: any) => {
        if (!this.fieldHasPickList(f)) return;
        const opts = this.resolvePickListOptions(f, formValues);
        this.pickListOptions[f.name] = this.ensureValueInOptions(
          opts,
          formValues?.[f.name],
          !!f?.ui_config?.multi_select,
        );
      });
    });
  }

  /**
   * Guard against a hydrated form value that has no matching option (the
   * underlying pick_list was reconfigured after the record was saved, or the
   * lookup pool hasn't loaded yet). Mat-select compares by reference/identity
   * so an unmatched value renders as an empty trigger and `mat-mdc-select-empty`
   * — surface a synthetic option carrying the raw value as label so the
   * select shows something instead of an empty box.
   */
  private ensureValueInOptions(opts: any[], value: any, isMulti: boolean): any[] {
    const out = Array.isArray(opts) ? [...opts] : [];
    const has = (v: any) => out.some(o => o?.value === v || String(o?.value) === String(v));
    const add = (v: any) => {
      if (v === undefined || v === null || v === '') return;
      if (!has(v)) out.unshift({ label: String(v), value: v });
    };
    if (isMulti && Array.isArray(value)) value.forEach(add);
    else add(value);
    return out;
  }

  /**
   * Resolve options for a single field. Mirrors the mobile
   * resolvePickListOptions but trimmed for the standalone web flow:
   * inline pick_list_values, pick_list "type.value", filter_by, pick_list_by.
   */
  private resolvePickListOptions(field: any, formValues: any): any[] {
    const inline = field?.ui_config?.pick_list_values;
    if (Array.isArray(inline) && inline.length > 0) {
      const mapped = inline.map((v: any) =>
        typeof v === 'string'
          ? { label: v, value: v }
          : { label: v.label || v.name || v.value, value: v.value ?? v.name },
      );
      // Mix Design Type is special — the hardcoded pick_list_values lists all
      // 28 AASHTOWare-supported types, but the org may only have mix_design
      // lookup rows for a subset (or none). The Mix Design ID picker below is
      // filter_by'd off mix_design_type, so showing types with zero matching
      // entries strands the user on an empty ID picker. Intersect with the
      // set of mix_design_type values actually present in the lookup pool so
      // the Type dropdown is always honest about what's available.
      if (field?.name === 'mix_design_type') {
        const pool: any[] =
          this.projectData?.lookups?.mix_design
          || this.appData?.lookups?.mix_design
          || [];
        const availableTypes = new Set(
          pool
            .map((item: any) => item?.extra_attributes?.mix_design_type)
            .filter((v: any) => v !== undefined && v !== null && v !== ''),
        );
        return mapped.filter((o: any) => availableTypes.has(o.value));
      }
      return mapped;
    }

    let pickListType: string | undefined = field?.ui_config?.pick_list?.split('.')?.[0];
    const pickListBy = field?.ui_config?.pick_list_by;
    if (!pickListType && pickListBy?.field && pickListBy?.values) {
      const parentValue = formValues?.[pickListBy.field];
      const mapped = parentValue ? pickListBy.values[parentValue] : null;
      if (mapped) pickListType = mapped.split('.')?.[0];
    }
    if (!pickListType) return [];

    if (pickListType === 'project_bid_items') {
      const bidItems = this.projectData?.bidSheet?.bidItems || [];
      return bidItems.map((bi: any) => {
        const itemCode = bi?.item || bi?.code;
        const description = bi?.description || bi?.short_description || '';
        const label = itemCode && description ? `${itemCode} - ${description}` : (itemCode || description || '(unnamed)');
        return { label, value: bi?.id };
      });
    }

    // Sampler ID-Name picks from the project's account list, formatted with
    // the AASHTOWare-side username (DOT\ssrinivasan → ssrinivasan) so the
    // value lines up with what AASHTOWare's SamplerId expects.
    if (field?.name === 'sampler_id') {
      return (this.projectAccounts || []).map((a: any) => {
        const aashto = a?.source_entity_extra_attributes?.user_name || '';
        const samplerName = aashto
          ? String(aashto).split('\\').pop()
          : (a?.email ? String(a.email).split('@')[0] : (a?.first_name || a?.uuid));
        return { label: samplerName, value: samplerName };
      }).filter((o: any) => !!o.value);
    }

    // Source picker — when the api has returned a (possibly material-
    // narrowed) list, prefer it over the lookup-derived `source_pit`
    // pool. Falls through to the lookup path when our list is empty
    // (e.g. before the initial fetch resolves) so the picker isn't
    // dead during hydration.
    if ((field?.name === 'source_id' || pickListType === 'source_pit') && this.sourceOptions.length > 0) {
      return this.sourceOptions;
    }

    // Test-method picker — same shape as source above. When the api
    // has returned a (possibly material-narrowed) list, prefer it
    // over the lookup-derived `org_material_test` pool. Falls through
    // to the lookup path when our list is empty so the picker isn't
    // dead during hydration.
    if (
      (field?.name === 'material_test_ids' ||
        pickListType === 'org_material_test' ||
        pickListType === 'material_test') &&
      this.testMethodOptions.length > 0
    ) {
      return this.testMethodOptions;
    }

    const projectLookups = this.projectData?.lookups?.[pickListType] || [];
    const appLookups = this.appData?.lookups?.[pickListType] || [];
    let pool = projectLookups.length > 0 ? projectLookups : appLookups;

    const filterBy = field?.ui_config?.filter_by;
    if (filterBy?.field && filterBy?.lookup_attribute) {
      const parentValue = formValues?.[filterBy.field];
      if (parentValue !== undefined && parentValue !== null && parentValue !== '') {
        pool = pool.filter(
          (item: any) => item?.extra_attributes?.[filterBy.lookup_attribute] === parentValue,
        );
      }
    }

    return pool.map((item: any) => ({
      label: item.name || item.value || String(item),
      value: item.value ?? item.name,
    }));
  }

  fieldHasPickList(field: any): boolean {
    return Array.isArray(field?.ui_config?.pick_list_values)
      ? field.ui_config.pick_list_values.length > 0
      : !!(field?.ui_config?.pick_list || field?.ui_config?.pick_list_by);
  }

  isMultiSelect(field: any): boolean {
    return !!field?.ui_config?.multi_select;
  }

  isDate(field: any): boolean {
    return field?.type === 'date';
  }

  isNumber(field: any): boolean {
    return field?.type === 'number';
  }

  isMultiline(field: any): boolean {
    return !!field?.ui_config?.multiline;
  }

  isDisplayOnly(field: any): boolean {
    return !!field?.ui_config?.display_only;
  }

  isFieldRequired(field: any): boolean {
    return !!field?.ui_config?.validation_rules?.required;
  }

  isLatLngField(field: any): boolean {
    return field?.name === 'latitude' || field?.name === 'longitude';
  }

  /** Fields whose ui_config.section_name === 'location' are collapsed into a popup. */
  isLocationSectionField(field: any): boolean {
    return field?.ui_config?.section_name === 'location';
  }

  /** True when at least one location field has a value — show the summary card. */
  hasLocationValue(): boolean {
    const names = ['station', 'station_plus', 'offset_distance', 'offset_direction', 'grade_reference', 'latitude', 'longitude'];
    return names.some(n => {
      const v = this.form?.get(n)?.value;
      return v !== null && v !== undefined && v !== '';
    });
  }

  /** Compact summary string for the location card (mirrors the mobile FormFieldPromptView). */
  locationSummary(): string {
    const f = (n: string) => this.form?.get(n)?.value;
    const parts: string[] = [];
    const station = f('station');
    if (station) {
      const plus = f('station_plus');
      parts.push(`Sta ${station}${plus ? '+' + plus : ''}`);
    }
    const dist = f('offset_distance');
    if (dist !== null && dist !== undefined && dist !== '') {
      const dir = f('offset_direction');
      parts.push(`${dist}${dir ? ' ' + dir : ''}`);
    }
    const grade = f('grade_reference');
    if (grade) parts.push(String(grade));
    const lat = f('latitude');
    const lng = f('longitude');
    if (lat && lng) parts.push(`${lat}, ${lng}`);
    return parts.join(' · ');
  }

  /**
   * Open the per-test entry side panel — web equivalent of the mobile
   * SampleTestEntryScreen. Snapshots the current parent-sample context
   * (sample_type, source_id, lab_control_number, …) into a read-only block,
   * passes any prior edits from `form.test_overrides[materialTestValue]`, and
   * registers an `onTestSaved` callback that stashes the returned edits back
   * onto the form. The api's `normalizePayload` expands the map into
   * `sample.tests[]` at submission time — same wiring as mobile.
   */
  openTestEntry(materialTestValue: string): void {
    const overrides: Record<string, any> = this.form.get('test_overrides')?.value || {};
    // Merge any persisted per-test data from the saved record (remarks +
    // attachments come back from getFullRecord on each test row) into the
    // existingEdits payload so the popup hydrates from the DB on first open.
    const savedTest = Array.isArray(this.data?.tests)
      ? this.data.tests.find((t: any) => t?.material_test_value === materialTestValue)
      : null;
    const existingEdits = {
      ...(savedTest ? {
        remarks: Array.isArray(savedTest.remarks) ? savedTest.remarks : [],
        pictures: Array.isArray(savedTest.pictures) ? savedTest.pictures : [],
        // result_values carries the agency-view group's saved fields plus
        // the reserved __children__ / __siblings__ keys for repeating
        // sub-groups (e.g. ATM 315 Riprap List rows). Hydrating from the
        // DB row on first open lets the popup round-trip child rows on
        // every reopen, not just within a single edit session.
        ...(savedTest.result_values && typeof savedTest.result_values === 'object'
          ? { result_values: savedTest.result_values }
          : {}),
      } : {}),
      ...(overrides[materialTestValue] || {}),
    };

    // chipLabel resolves the lookup label for the multi-select value.
    const matField = this.groupConfigs
      .flatMap((g: any) => g.fields || [])
      .find((f: any) => f?.name === 'material_test_ids');
    const materialTestLabel = matField
      ? this.chipLabel(matField, materialTestValue)
      : materialTestValue;

    const parentContext = {
      sampleType: this.form.get('sample_type')?.value || this.data?.sample_type || '',
      sourceId: this.form.get('source_id')?.value || this.data?.source_value || this.data?.source_id || '',
      labControlNumber: this.form.get('lab_control_number')?.value || this.data?.lab_control_number || '',
      regionalLabNumber: this.form.get('regional_lab_number')?.value || this.data?.regional_lab_number || '',
      contractId: this.projectData?.source_entity_id || this.data?.contract_id || '',
      labUnit: this.form.get('lab_unit')?.value || this.data?.lab_unit || '',
    };

    // Open as a modal dialog so the parent sample form stays visible/editable
    // behind the popup (instead of being replaced by the right panel).
    this.dialog.open(SampleTestEntryComponent, {
      disableClose: false,
      width: '720px',
      maxHeight: '90vh',
      panelClass: 'sample-test-entry-dialog',
      data: {
        materialTestValue,
        materialTestLabel,
        parentContext,
        existingEdits,
        readOnly: this.readOnly,
        onTestSaved: (value: string, edits: any) => {
          const current = { ...(this.form.get('test_overrides')?.value || {}) };
          current[value] = edits;
          this.form.get('test_overrides')?.setValue(current);
          this.commonService.notification('Test details saved', 'success');
        },
      },
    });
  }

  openLocationPopup(generalGroup: any): void {
    if (this.readOnly) return;
    const locationFields = (generalGroup?.fields || []).filter((fd: any) => this.isLocationSectionField(fd));
    const offsetDirField = locationFields.find((fd: any) => fd.name === 'offset_direction');
    this.dialog.open(SampleLocationPopupComponent, {
      disableClose: false,
      width: '552px',
      maxHeight: '90vh',
      data: {
        form: this.form,
        fields: locationFields,
        offsetDirectionOptions: offsetDirField ? (this.pickListOptions[offsetDirField.name] || []) : [],
      },
    });
  }

  /**
   * Resolve the display string for a display-only field, mapping the form
   * field name → DB column name where they differ. The custom_field config
   * uses *_id suffixes for context-derived fields (sample_id, source_id,
   * material_code, sample_status), but the DB stores them under different
   * column names (name, source_value, material.name via material_name,
   * ref_sample_status).
   */
  displayValueFor(field: any): string {
    // Sample ID + Lab Control Number are AASHTOWare-assigned identifiers —
    // surface a "will be populated on submit" placeholder until the sample
    // has been pushed. Form state retains the underlying values so the push
    // request still carries them.
    if (
      !this.isPushed()
      && (field?.name === 'sample_id' || field?.name === 'lab_control_number')
    ) {
      return 'Pending';
    }

    const formValue = this.form.get(field?.name)?.value;
    if (formValue !== undefined && formValue !== null && formValue !== '') return String(formValue);
    const direct = this.data?.[field?.name];
    if (direct !== undefined && direct !== null && direct !== '') return String(direct);
    const aliasMap: Record<string, string> = {
      sample_id: 'name',
      source_id: 'source_value',
      sampler_id: 'sampler_value',
      mix_design_id: 'mix_design_value',
      material_code: 'material_name',
      sample_status: 'ref_sample_status',
      field_number: 'control_number',
    };
    const aliasKey = aliasMap[field?.name];
    if (aliasKey && this.data?.[aliasKey] !== undefined && this.data[aliasKey] !== null && this.data[aliasKey] !== '') {
      return String(this.data[aliasKey]);
    }
    return '-';
  }

  /** Toggle a value into a multi-select array. */
  onMultiSelectAdd(field: any, value: any): void {
    if (!value) return;
    const current = this.form.get(field.name)?.value || [];
    if (current.includes(value)) return;
    this.form.get(field.name)?.setValue([...current, value]);
    this.recomputePickListOptions();
  }

  onMultiSelectRemove(field: any, value: any): void {
    const current: any[] = this.form.get(field.name)?.value || [];
    this.form.get(field.name)?.setValue(current.filter(v => v !== value));
    this.recomputePickListOptions();
  }

  /** "Add source" / "Add another source" — the submittals add-link wording. */
  addRowLabel(group: any): string {
    const label = String(group?.label || 'row').trim();
    const singular = label.replace(/s$/i, '').toLowerCase();
    const count = (this.rows[group?.key] || []).length;
    return count ? `Add another ${singular}` : `Add ${singular}`;
  }

  // Repeating group helpers
  addRow(groupKey: string, fields: any[]): void {
    if (!this.rows[groupKey]) this.rows[groupKey] = [];
    this.rows[groupKey].push(this.buildRowFormGroup(fields, {}));
    this.recomputePickListOptions();
  }

  removeRow(groupKey: string, idx: number): void {
    if (!this.rows[groupKey]) return;
    this.rows[groupKey].splice(idx, 1);
    this.recomputePickListOptions();
  }

  // ---- Pictures (attachments) ----------------------------------------------

  /**
   * Mirrors the RFI add-button menu — the bottom-bar paperclip icon opens
   * this menu and the user picks "Attachments" to trigger the file input.
   * Kept generic so we can later add more options (videos, documents, etc.).
   */
  addMenuOptions = [
    {
      key: 'picture',
      icon: 'paperclip',
      label: 'Attachments',
      handler: () => this.openFilePicker(),
    },
  ];

  openFilePicker(): void {
    if (this.readOnly) return;
    this.fileInputRef?.nativeElement?.click();
  }

  /**
   * Existing attachments fetched from the api have a `mime` column but not
   * the `fileType` discriminator that <app-pictures-section-with-tags> uses
   * to pick a renderer. The shared upload effect sets it on freshly-uploaded
   * items (shared.effects.ts ~line 128) — derive the same here so hydrated
   * thumbnails actually render.
   */
  private normalizePicture(p: any): any {
    if (!p) return p;
    if (p.fileType) return p;
    const mime = p.mime || '';
    let fileType: string | undefined;
    if (mime.startsWith('image/')) fileType = 'picture';
    else if (mime.startsWith('video/')) fileType = 'video';
    else if (mime.startsWith('application/') || mime.startsWith('text/')) fileType = 'document';
    return fileType ? { ...p, fileType } : { ...p, fileType: 'picture' };
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    const formData = new FormData();
    let hasFile = false;
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file) {
        formData.append('inputFiles', file);
        hasFile = true;
      }
    }
    if (hasFile) {
      this.store.dispatch(UploadFileRequest({ payload: formData }));
    }
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  removePicture(pic: any): void {
    this.pictures = this.pictures.filter(p => p.id !== pic.id);
    this.publishMediaData();
  }

  // ---- Load / Save / Push --------------------------------------------------

  private loadFullRecord(uuid: string): void {
    this.loading = true;
    this.sampleRecordsService
      .fetch(uuid)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          const rec = resp?.data || resp;
          if (rec) {
            this.record = rec;
            // Re-merge full record into `data` so initializeForm() picks up
            // sources/associations/contract_items/tests/remarks/pictures.
            this.data = { ...this.data, ...rec };
            if (Array.isArray(rec.pictures)) {
              this.pictures = rec.pictures.map((p: any) => this.normalizePicture(p));
              this.publishMediaData();
            }
            this.initializeForm();
          }
        },
        error: err => {
          this.loading = false;
          this.errorMessage = err?.error?.message || err?.message || 'Failed to load sample record';
          this.commonService.notification(this.errorMessage, 'error');
        },
      });
  }

  private buildPayload(): any {
    const formValues = this.form.getRawValue();
    const rowsToArray = (key: string): any[] =>
      (this.rows[key] || []).map(g => g.getRawValue());
    // `formValues` already carries `test_overrides` (added as a form control
    // in initializeForm); the api's normalizePayload reads it and merges the
    // per-test edits into sample.tests[] at submission time.
    // Sources is rendered as a flat multi-select (additional_source_ids) +
    // primary source — expand the multi-select into the `sources` array shape
    // the api expects (objects with source_value), preserving sequence so the
    // existing rows on edit reuse their primary key and don't soft-delete.
    const additionalSourceIds: any[] = Array.isArray(formValues?.additional_source_ids)
      ? formValues.additional_source_ids.filter((v: any) => v !== undefined && v !== null && v !== '')
      : [];
    const sources = additionalSourceIds.map((source_value: any, idx: number) => ({
      source_value,
      sequence: idx + 1,
    }));

    const payload: any = {
      ...formValues,
      project_id: this.projectId,
      sources,
      associations: rowsToArray('associations'),
      remarks: rowsToArray('remarks'),
      pictures: this.pictures.map(p => p.id).filter(Boolean),
    };
    // When opened inside a DWR, link the row to the daily report and the
    // bid item / material set so the api can scope the embedded list back
    // to the same (daily_report_id, bid_item_material_set_id) tuple.
    if (this.embeddedContext) {
      if (this.embeddedContext.dailyReportId !== undefined && this.embeddedContext.dailyReportId !== null) {
        payload.daily_report_id = this.embeddedContext.dailyReportId;
      }
      if (this.embeddedContext.bidItemId !== undefined && this.embeddedContext.bidItemId !== null) {
        payload.bid_item_id = this.embeddedContext.bidItemId;
      }
      if (this.embeddedContext.bidItemMaterialSetId !== undefined && this.embeddedContext.bidItemMaterialSetId !== null) {
        payload.bid_item_material_set_id = this.embeddedContext.bidItemMaterialSetId;
      }
    }
    return payload;
  }

  save(): void {
    if (this.saving || this.readOnly) return;
    this.saving = true;
    this.errorMessage = null;
    const payload = this.buildPayload();
    const uuid = this.record?.uuid || this.data?.uuid;

    const req$ = uuid
      ? this.sampleRecordsService.update(uuid, payload)
      : this.sampleRecordsService.create(payload);

    req$.pipe(takeUntil(this.onDestroy)).subscribe({
      next: (resp: any) => {
        this.saving = false;
        const rec = resp?.data || resp;
        if (rec) {
          this.record = rec;
          this.data = { ...this.data, ...rec };
        }
        this.commonService.notification('Sample record saved', 'success');
        this.sampleRecordsService.refresh.next();
      },
      error: err => {
        this.saving = false;
        this.errorMessage = err?.error?.message || err?.message || 'Save failed';
        this.commonService.notification(this.errorMessage, 'error');
      },
    });
  }

  push(): void {
    if (this.pushing || this.readOnly) return;
    const uuid = this.record?.uuid || this.data?.uuid;
    if (!uuid) {
      this.commonService.notification('Save the sample first before pushing', 'error');
      return;
    }
    this.pushing = true;
    this.errorMessage = null;
    this.sampleRecordsService
      .push(uuid)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.pushing = false;
          const rec = resp?.data || resp;
          if (rec) {
            this.record = { ...this.record, ...rec };
            this.data = { ...this.data, ...rec };
            // Mirror the AASHTOWare-assigned identifiers into the existing
            // form controls so the General → Context sub-section flips from
            // "Pending" to the real values without re-mounting the form.
            if (rec.name && this.form.contains('sample_id')) {
              this.form.get('sample_id')?.setValue(rec.name, { emitEvent: false });
            }
            if (rec.lab_control_number && this.form.contains('lab_control_number')) {
              this.form.get('lab_control_number')?.setValue(rec.lab_control_number, { emitEvent: false });
            }
          }
          this.commonService.notification('Pushed to AASHTOWare', 'success');
          this.sampleRecordsService.refresh.next();
        },
        error: err => {
          this.pushing = false;
          this.errorMessage =
            err?.error?.error
            || err?.error?.message
            || err?.message
            || 'Push failed';
          this.commonService.notification(this.errorMessage, 'error');
        },
      });
  }

  close(): void {
    this.projectsService.closeRightPanel.next();
  }

  isPushed(): boolean {
    return !!(this.record?.source_entity_id || this.data?.source_entity_id);
  }

  formatInProjectTimezone(timestamp: string, format: string): string {
    if (!timestamp) return '';
    const tz = this.projectTimezone || 'UTC';
    return moment.utc(timestamp).tz(tz).format(format);
  }

  trackByName(_idx: number, item: any): string {
    return item?.name || item?.uuid || String(_idx);
  }

  trackByValue(_idx: number, value: any): any {
    return value;
  }

  /**
   * For a multi-select chip, resolve its label by looking up the value in
   * the field's pickListOptions. Falls back to the raw value when no match
   * (which can happen if the underlying lookup hasn't loaded yet).
   */
  chipLabel(field: any, value: any, group?: any, rowIdx?: number): string {
    const opts = (group !== undefined && rowIdx !== undefined)
      ? this.rowPickListOptions[`${group.key}-${rowIdx}-${field.name}`]
      : this.pickListOptions[field.name];
    const match = (opts || []).find(o => o.value === value || String(o.value) === String(value));
    return match?.label ?? String(value);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
